import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button } from "reactstrap";
import { useService } from "../../context/ServiceContext";
import { v4 as uuidv4 } from "uuid"; // For generating unique userSession UUID
import { useEtim } from "../../context/ETIMContext";
import AppTable from "../Tables/AppTable";
import BMEProductTree from "./BMEProductTree";
import LoadedContent from "../LoadedContent";
import { FormattedMessage, useIntl } from "react-intl";

const ExportBMECatModal = ({ isOpen, toggle }) => {
  const [companies, setCompanies] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [buyerName, setBuyerName] = useState("");
  const [versionETIM, setVersionETIM] = useState("8.0");
  const [flagEAN, setFlagEAN] = useState(false);
  const [flagTarifa, setFlagTarifa] = useState(false);
  const [flagObsoleto, setFlagObsoleto] = useState(false);
  const [productTree, setProductTree] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState({});
  const [selectedGroups, setSelectedGroups] = useState({});
  const [selectedSeries, setSelectedSeries] = useState({});
  const [selectedCatalogues, setSelectedCatalogues] = useState({});
  const [xmlStructure, setXmlStructure] = useState([]);
  const [structureModifications, setStructureModifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { versions } = useEtim();
  const intl = useIntl();

  const { bmeCatCompaniesProvider, bmeCatProductsProvider, bmeCatLanguagesProvider, bmeCatXMLStructureProvider, bmeCatStore } = useService();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [companiesData, languagesData, structureData] = await Promise.all([bmeCatCompaniesProvider.get(), bmeCatLanguagesProvider.get(), bmeCatXMLStructureProvider.get()]);
        setCompanies(companiesData);
        setLanguages(languagesData);
        setXmlStructure(structureData);
      } catch (err) {
        setError("Failed to load initial data");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      if (!selectedCompany || !selectedLanguage) return;
      try {
        setLoading(true);
        setError(null);
        const products = await bmeCatProductsProvider.get({
          languageId: selectedLanguage,
          companyId: selectedCompany,
          versionETIM: versionETIM,
          flagEAN,
          flagTarifa,
          flagObsoleto,
        }); // Calls ETIM.spCargarBMEcatTree
        setProductTree(generateProductsTree(products));
      } catch (err) {
        setError("Failed to load product tree");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [selectedCompany, selectedLanguage, versionETIM, flagEAN, flagTarifa, flagObsoleto]);

  const generateProductsTree = (products) => {
    const tree = {};
    products.forEach((prod) => {
      const { catalogueId, catalogueDescription, groupDescription, seriesId, seriesName, productCode, productName } = prod;
      if (!tree[catalogueId]) {
        tree[catalogueId] = { catalogueId, catalogueDescription, groups: {} };
      }
      const catalogue = tree[catalogueId];
      if (!catalogue.groups[groupDescription]) {
        catalogue.groups[groupDescription] = { groupDescription, series: {} };
      }
      const group = catalogue.groups[groupDescription];
      if (!group.series[seriesId]) {
        group.series[seriesId] = { seriesId, seriesName, products: [] };
      }
      group.series[seriesId].products.push({ productCode, productName });
    });
    return Object.values(tree);
  };

  const handleStructureChange = (id, field, value) => {
    setStructureModifications((prev) => {
      const existing = prev.find((m) => m.id === id) || { id };
      return [...prev.filter((m) => m.id !== id), { ...existing, [field]: value }];
    });
  };

  const handleProductSelection = (productCode, checked) => {
    setSelectedProducts((prev) => ({ ...prev, [productCode]: checked }));
  };

  const handleSeriesSelection = (seriesId, catalogueId, groupDescription, checked) => {
    setSelectedSeries((prev) => ({
      ...prev,
      [`${catalogueId}-${groupDescription}-${seriesId}`]: checked,
    }));
  };

  const handleGroupSelection = (groupDescription, catalogueId, checked) => {
    setSelectedGroups((prev) => ({
      ...prev,
      [`${catalogueId}-${groupDescription}`]: checked,
    }));
  };

  const handleCatalogueSelection = (catalogueId, checked) => {
    setSelectedCatalogues((prev) => ({
      ...prev,
      [catalogueId]: checked,
    }));
  };

  const handleGenerateXML = async () => {
    if (!selectedCompany || !selectedLanguage || !buyerName || !Object.keys(selectedProducts).some((key) => selectedProducts[key])) {
      setError("Please fill all required fields and select at least one product.");
      return;
    }

    setLoading(true);
    setError(null);
    const userSession = uuidv4();

    const selectedItems = Object.keys(selectedProducts)
      .filter((key) => selectedProducts[key])
      .map((productCode) => {
        for (const catalogue of productTree) {
          for (const group of Object.values(catalogue.groups)) {
            for (const series of Object.values(group.series)) {
              const product = series.products.find((p) => p.productCode === productCode);
              if (product) {
                return {
                  catalogId: catalogue.catalogueId,
                  seriesId: series.seriesId,
                  productCode: product.productCode,
                };
              }
            }
          }
        }
        return null;
      })
      .filter((item) => item !== null);

    const structure = xmlStructure
      .filter((row) => {
        const modification = structureModifications.find((m) => m.id === row.id);
        return modification && modification.incluir ? modification : row.incluir;
      })
      .map((row) => ({
        id: row.id,
        elementName: row.elementName ?? "",
        level: row.level,
        valorFijo: row.valorFijo ?? "",
        consultaSQL: row.consultaSQL ?? "",
        atributoXML: row.atributoXML ?? "",
        incluir: row.incluir,
        esquema: row.esquema ?? "",
        obligatorio: row.obligatorio,
        attributeBMECAT: row.attributeBMECAT ?? "",
      }));

    const payload = {
      userSession,
      companyId: selectedCompany,
      languageId: selectedLanguage,
      versionETIM,
      flagEAN,
      flagTarifa,
      flagObsoleto,
      buyerName,
      structure,
      selectedItems,
    };

    try {
      const response = await bmeCatStore.store(payload, { responseType: "blob" });

      const binaryString = atob(response.fileContents);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const url = window.URL.createObjectURL(new Blob([bytes], { type: response.contentType || "application/xml" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "bmecat_export.xml");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toggle();
    } catch (error) {
      setError("Failed to generate XML");
      console.error("Error generating XML:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "ID",
        accessor: "id",
        Cell: ({ row }) => (
          <Input
            type="number"
            value={structureModifications.find((m) => m.id === row.original.id)?.id ?? row.original.id}
            onChange={(e) => handleStructureChange(row.original.id, "id", parseInt(e.target.value))}
            disabled={loading}
            bsSize="sm"
            aria-label={`ID for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "Element Name",
        accessor: "elementName",
        Cell: ({ row }) => (
          <Input
            type="text"
            value={structureModifications.find((m) => m.id === row.original.id)?.elementName || row.original.elementName || ""}
            onChange={(e) => handleStructureChange(row.original.id, "elementName", e.target.value)}
            disabled={loading}
            bsSize="sm"
            aria-label={`Element Name for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "Level",
        accessor: "level",
        Cell: ({ row }) => (
          <Input
            type="number"
            value={structureModifications.find((m) => m.id === row.original.id)?.level ?? row.original.level}
            onChange={(e) => handleStructureChange(row.original.id, "level", parseInt(e.target.value))}
            disabled={loading}
            bsSize="sm"
            aria-label={`Level for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "Fixed Value",
        accessor: "valorFijo",
        Cell: ({ row }) => (
          <Input
            type="text"
            value={structureModifications.find((m) => m.id === row.original.id)?.valorFijo || row.original.valorFijo || ""}
            onChange={(e) => handleStructureChange(row.original.id, "valorFijo", e.target.value)}
            disabled={loading}
            bsSize="sm"
            aria-label={`Fixed Value for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "SQL Query",
        accessor: "consultaSQL",
        Cell: ({ row }) => (
          <Input
            type="text"
            value={structureModifications.find((m) => m.id === row.original.id)?.consultaSQL || row.original.consultaSQL || ""}
            onChange={(e) => handleStructureChange(row.original.id, "consultaSQL", e.target.value)}
            disabled={loading}
            bsSize="sm"
            aria-label={`SQL Query for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "XML Attribute",
        accessor: "atributoXML",
        Cell: ({ row }) => (
          <Input
            type="text"
            value={structureModifications.find((m) => m.id === row.original.id)?.atributoXML || row.original.atributoXML || ""}
            onChange={(e) => handleStructureChange(row.original.id, "atributoXML", e.target.value)}
            disabled={loading}
            bsSize="sm"
            aria-label={`XML Attribute for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "Include",
        accessor: "incluir",
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={structureModifications.find((m) => m.id === row.original.id)?.incluir ?? row.original.incluir}
            onChange={(e) => handleStructureChange(row.original.id, "incluir", e.target.checked)}
            disabled={loading}
            aria-label={`Include for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "Schema",
        accessor: "esquema",
        Cell: ({ row }) => (
          <Input
            type="text"
            value={structureModifications.find((m) => m.id === row.original.id)?.esquema || row.original.esquema || ""}
            onChange={(e) => handleStructureChange(row.original.id, "esquema", e.target.value)}
            disabled={loading}
            bsSize="sm"
            aria-label={`Schema for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "Mandatory",
        accessor: "obligatorio",
        Cell: ({ row }) => (
          <input
            type="checkbox"
            className=""
            checked={structureModifications.find((m) => m.id === row.original.id)?.obligatorio ?? row.original.obligatorio}
            onChange={(e) => handleStructureChange(row.original.id, "obligatorio", e.target.checked)}
            disabled={loading}
            aria-label={`Mandatory for row ${row.original.id}`}
          />
        ),
      },
      {
        Header: "BMECAT Attribute",
        accessor: "attributeBMECAT",
        Cell: ({ row }) => (
          <Input
            type="text"
            value={structureModifications.find((m) => m.id === row.original.id)?.attributeBMECAT || row.original.attributeBMECAT || ""}
            onChange={(e) => handleStructureChange(row.original.id, "attributeBMECAT", e.target.value)}
            disabled={loading}
            bsSize="sm"
            aria-label={`BMECAT Attribute for row ${row.original.id}`}
          />
        ),
      },
    ],
    [structureModifications, loading]
  );

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>
        <FormattedMessage id="etim.export_bmecat" defaultMessage="Export BMECat" />
        </ModalHeader>

      <ModalBody>
        <LoadedContent loading={loading} overlay={true}>
          {error && <div className="alert alert-danger">{error}</div>}
          <Card>
            <CardHeader><FormattedMessage id="etim.export_settings" defaultMessage="Export Settings" /></CardHeader>
            <CardBody>
              <Form>
                <Row>
                  <Col md="3">
                    <FormGroup>
                      <Label for="company"><FormattedMessage id="etim.company" defaultMessage="Company" /> *</Label>
                      <Input type="select" id="company" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} disabled={loading}>
                        <option value="">{intl.formatMessage({id:"etim.select_company",defaultMessage:"Select Company" })}</option>
                        {companies.map((company) => (
                          <option key={company.key} value={company.key}>
                            {company.value}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="language"><FormattedMessage id="certificates.language" defaultMessage="Language" /> *</Label>
                      <Input type="select" id="language" value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)} disabled={loading}>
                        <option value="">{intl.formatMessage({id:"etim.select_language",defaultMessage:"Select Language" })}</option>
                        {languages.map((lang) => (
                          <option key={lang.key} value={lang.key}>
                            {lang.value}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="buyerName"><FormattedMessage id="etim.buyer_name" defaultMessage="Buyer Name" /> *</Label>
                      <Input type="text" id="buyerName" value={buyerName} onChange={(e) => setBuyerName(e.target.value)} disabled={loading} />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup>
                      <Label for="etimVersion"><FormattedMessage id="etim.etim_version" defaultMessage="Versión ETIM" /></Label>
                      <Input type="select" id="etimVersion" value={versionETIM} onChange={(e) => setVersionETIM(e.target.value)} disabled={loading}>
                        {versions.map((version) => (
                          <option key={version.version} value={version.version}>
                            {version.version}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
                <Row></Row>
                <Row>
                  <Col md="3">
                    <FormGroup check>
                      <Input type="checkbox" id="flagEAN" checked={flagEAN} onChange={(e) => setFlagEAN(e.target.checked)} disabled={loading} />
                      <Label check for="flagEAN">
                        <FormattedMessage id="etim.etim_version" defaultMessage="Include EAN" />
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup check>
                      <Input type="checkbox" id="flagTarifa" checked={flagTarifa} onChange={(e) => setFlagTarifa(e.target.checked)} disabled={loading} />
                      <Label check for="flagTarifa">
                        <FormattedMessage id="etim.include_tariff" defaultMessage="Include Tariff" />
                      </Label>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup check>
                      <Input type="checkbox" id="flagObsoleto" checked={flagObsoleto} onChange={(e) => setFlagObsoleto(e.target.checked)} disabled={loading} />
                      <Label check for="flagObsoleto">
                        <FormattedMessage id="etim.include_obsolete" defaultMessage="Include Obsolete" />
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
          <Card className="mt-3">
            <CardBody>
              <Row>
                <Col md="3" style={{ maxHeight: "80vh", overflowY: "auto" }}>
                  <h2><FormattedMessage id="app.product" defaultMessage="Products" /></h2>
                  {loading ? (
                    <p>{intl.formatMessage({id:"etim.loading_product",defaultMessage:"Loading product tree" })}...</p>
                  ) : (
                    <BMEProductTree
                      productTree={productTree}
                      loading={loading}
                      selectedProducts={selectedProducts}
                      handleProductSelection={handleProductSelection}
                      selectedGroups={selectedGroups}
                      handleGroupSelection={handleGroupSelection}
                      selectedSeries={selectedSeries}
                      handleSeriesSelection={handleSeriesSelection}
                      selectedCatalogues={selectedCatalogues}
                      handleCatalogueSelection={handleCatalogueSelection}
                    />
                  )}
                </Col>
                <Col md="9">
                  <h2><FormattedMessage id="etim.xml_structure" defaultMessage="XML Structure" /></h2>
                  {loading ? (
                    <p>{intl.formatMessage({id:"etim.loading_xml",defaultMessage:"Loading XML structure" })}... </p>
                  ) : xmlStructure.length > 0 ? (
                    <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
                      <AppTable columns={columns} data={xmlStructure} loading={loading} enablePagination={false} className="table-sm" style={{ fontSize: "0.85rem" }} />
                    </div>
                  ) : (
                    <p><FormattedMessage id="etim.no_xml_structure" defaultMessage="No XML structure data available." /></p>
                  )}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </LoadedContent>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cancel
        </Button>
        <Button
          color="success"
          onClick={handleGenerateXML}
          disabled={loading || !selectedCompany || !selectedLanguage || !buyerName || !Object.keys(selectedProducts).some((key) => selectedProducts[key])}
        >
          {loading ? "Generating..." : <FormattedMessage id="etim.generate_xml" defaultMessage="Generate XML" />}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ExportBMECatModal;
